/*
 * Every block and template preview in the docs. Each entry renders on its own full-screen page
 * at /view/<id> (src/pages/view/[id].tsx), which the docs frames load for the width switch.
 * render lives in src/components/docs/view-registry.tsx so the docs pages stay light.
 */
export type ViewGroup = 'blocks' | 'templates'

export interface ViewMeta {
    id: string
    group: ViewGroup
    category: string
    title: string
    description: string
    height: number
    dark?: boolean
    hidden?: boolean
    source: string
    usage: string
}

export interface ViewCategory {
    group: ViewGroup
    slug: string
    title: string
    figma: string
    description: string
}

export const VIEW_CATEGORIES: ViewCategory[] = [
    { group: 'blocks', slug: 'app-layout', title: 'App layout', figma: '20 / Blocks / App layout', description: 'The sidebar, top bar, page header, containers, column grids and page bodies every GoodUnited screen is built from.' },
    { group: 'blocks', slug: 'chart-content', title: 'Chart content', figma: '21 / Blocks / Chart content', description: 'Ready-made chart cards built from the shared chart anatomy.' },
    { group: 'blocks', slug: 'drawers', title: 'Drawers', figma: '22 / Blocks / Drawers', description: 'The reusable pieces that fill a drawer, and the content recipes for the analytics and editor drawers.' },
    { group: 'blocks', slug: 'kanban', title: 'Kanban', figma: '23 / Blocks / Kanban', description: 'Cards, stages, columns, organization swimlanes and the toolbar, plus the inbox and empty boards.' },
    { group: 'blocks', slug: 'tables', title: 'Tables', figma: '24 / Blocks / Tables', description: 'The five GoodUnited tables, from plain records to tool performance.' },
    { group: 'blocks', slug: 'supporter-response', title: 'Supporter Response', figma: '10 / Components / Chat', description: 'The contact list, the conversation in its three states, the supporter profile and the writing-style settings that make up the Supporter Response inbox.' },
    { group: 'templates', slug: 'page-layouts', title: 'Page layouts', figma: '30 / Templates / Page layouts', description: 'Ten starters built from the shared shell: column layouts, Dashboard, Workspace, Board and the dark version.' },
    { group: 'templates', slug: 'drawers', title: 'Drawers', figma: '31 / Templates / Drawers', description: 'The empty drawer in its three widths and the composed drawers, opening over the dashboard in light and dark.' },
    { group: 'templates', slug: 'kanban', title: 'Kanban', figma: '32 / Templates / Kanban', description: 'The Inbox and empty boards in light and dark. Open a card to review it in the editor drawer.' },
    { group: 'templates', slug: 'supporter-response', title: 'Supporter Response', figma: '33 / Templates / Supporter Response', description: 'The inbox as a Messenger draft, a message sent and an email marked as sent, plus the writing-style settings, in light and dark. Every one is live.' }
]

const LAYOUT = 'src/components/blocks/app-layout.tsx'
const CHARTS = 'src/components/blocks/chart-blocks.tsx'
const DRAWERS = 'src/components/blocks/drawer-blocks.tsx'
const KANBAN = 'src/components/blocks/kanban.tsx'
const TABLES = 'src/components/blocks/tables.tsx'
const PAGE_LAYOUTS = 'src/components/templates/page-layouts.tsx'
const DRAWER_TEMPLATES = 'src/components/templates/drawers.tsx'
const KANBAN_TEMPLATES = 'src/components/templates/kanban.tsx'
const CHAT_BLOCKS = 'src/components/blocks/supporter-response.tsx'
const WRITING_STYLE = 'src/components/blocks/writing-style-settings.tsx'
const CHAT_TEMPLATES = 'src/components/templates/supporter-response.tsx'

const pageLayout = (id: string, kind: string, title: string, description: string, dark = false): ViewMeta => ({
    id,
    group: 'templates',
    category: 'page-layouts',
    title,
    description,
    height: 760,
    dark,
    source: PAGE_LAYOUTS,
    usage: `import { PageLayoutTemplate } from '@/components/templates/page-layouts'\n\n<PageLayoutTemplate kind='${kind}' />`
})

const supporterResponseTemplate = (id: string, kind: string, title: string, description: string, dark = false): ViewMeta => ({
    id,
    group: 'templates',
    category: 'supporter-response',
    title,
    description,
    height: 900,
    dark,
    source: CHAT_TEMPLATES,
    usage: `import { SupporterResponseTemplate } from '@/components/templates/supporter-response'\n\n<SupporterResponseTemplate kind='${kind}' />`
})

const drawerTemplate = (id: string, kind: string, title: string, description: string, dark = false): ViewMeta => ({
    id,
    group: 'templates',
    category: 'drawers',
    title,
    description,
    height: 820,
    dark,
    source: DRAWER_TEMPLATES,
    usage: `import { DrawerTemplate } from '@/components/templates/drawers'\n\n<DrawerTemplate kind='${kind}' />`
})

export const VIEWS: ViewMeta[] = [
    {
        id: 'dashboard-overview',
        group: 'templates',
        category: 'drawers',
        title: 'Insights dashboard',
        description: 'The dashboard the drawers open over: metrics, revenue, compact charts and acquisition channels.',
        height: 860,
        hidden: true,
        source: DRAWER_TEMPLATES,
        usage: `import { DrawerTemplate } from '@/components/templates/drawers'\n\n<DrawerTemplate kind='revenue-people' defaultOpen={false} />`
    },
    {
        id: 'app-sidebar',
        group: 'blocks',
        category: 'app-layout',
        title: 'AppSidebar',
        description: 'Navigation built from SidebarMenuButton and SidebarMenuSubButton. The header (logo with an inset divider) and the footer stay outside the scrolling area; every group starts closed except the active one.',
        height: 760,
        source: LAYOUT,
        usage: `import { AppSidebar } from '@/components/blocks/app-layout'\n\n<AppSidebar activeItem='insights' />`
    },
    {
        id: 'app-topbar',
        group: 'blocks',
        category: 'app-layout',
        title: 'AppTopbar',
        description: 'Starts with a divider, then the breadcrumb with the section icon and page title, the optional date control and the account avatar.',
        height: 300,
        source: LAYOUT,
        usage: `import { AppTopbar } from '@/components/blocks/app-layout'\n\n<AppTopbar title='Page title' showDate />`
    },
    {
        id: 'page-header',
        group: 'blocks',
        category: 'app-layout',
        title: 'PageHeader',
        description: 'One shared page heading with an optional description and an Actions slot.',
        height: 280,
        source: LAYOUT,
        usage: `import { PageHeader } from '@/components/blocks/app-layout'\n\n<PageHeader\n    title='Inbox'\n    description='AI-recommended actions, reviewed and approved by your GoodUnited team'\n    actions={<Button>Add action</Button>}\n/>`
    },
    {
        id: 'layout-container',
        group: 'blocks',
        category: 'app-layout',
        title: 'Layout/Container',
        description: 'Empty Content slots: card for dashboards and forms, panel for flush workspaces, lane for boards.',
        height: 340,
        source: LAYOUT,
        usage: `import { LayoutContainer } from '@/components/blocks/app-layout'\n\n<LayoutContainer variant='card'>{/* content */}</LayoutContainer>\n<LayoutContainer variant='panel'>{/* content */}</LayoutContainer>\n<LayoutContainer variant='lane'>{/* cards */}</LayoutContainer>`
    },
    {
        id: 'layout-columns',
        group: 'blocks',
        category: 'app-layout',
        title: 'Layout/Columns',
        description: 'Responsive grid tracks 1, 2, 3, 4, 2:1 and 1:2. Each column holds its own Layout/Container.',
        height: 1040,
        source: LAYOUT,
        usage: `import { LayoutColumns, LayoutContainer } from '@/components/blocks/app-layout'\n\n<LayoutColumns track='2:1'>\n    <LayoutContainer />\n    <LayoutContainer />\n</LayoutColumns>`
    },
    {
        id: 'page-bodies',
        group: 'blocks',
        category: 'app-layout',
        title: 'PageBody and WorkspaceBody',
        description: 'PageBody supplies the shared spacing and PageHeader. WorkspaceBody fills the page with edge-to-edge panels.',
        height: 900,
        source: LAYOUT,
        usage: `import { PageBody, WorkspaceBody, LayoutContainer } from '@/components/blocks/app-layout'\n\n<PageBody title='Page title'>{/* content */}</PageBody>\n\n<WorkspaceBody>\n    <LayoutContainer variant='panel' />\n    <LayoutContainer variant='panel' />\n    <LayoutContainer variant='panel' />\n</WorkspaceBody>`
    },
    {
        id: 'composed-layouts',
        group: 'blocks',
        category: 'app-layout',
        title: 'Composed layout blocks',
        description: 'Dashboard, Workspace and Board presets combining the same containers and column grids.',
        height: 1600,
        source: LAYOUT,
        usage: `import { DashboardLayout, WorkspaceLayout, BoardLayout } from '@/components/blocks/app-layout'\n\n<DashboardLayout />\n<WorkspaceLayout />\n<BoardLayout />`
    },
    {
        id: 'app-shell',
        group: 'blocks',
        category: 'app-layout',
        title: 'AppShell',
        description: 'Sidebar, top bar and the white page, whose corner against the navigation keeps its radius while it scrolls. On phones the sidebar slides out from the menu button.',
        height: 760,
        source: LAYOUT,
        usage: `import { AppShell, PageBody } from '@/components/blocks/app-layout'\n\n<AppShell title='Page title'>\n    <PageBody title='Page title'>{/* content */}</PageBody>\n</AppShell>`
    },
    {
        id: 'chart-area-compact',
        group: 'blocks',
        category: 'chart-content',
        title: 'Chart/Area/Compact',
        description: 'A compact card for one segment: value, people, an area plot and the reporting period.',
        height: 480,
        source: CHARTS,
        usage: `import { ChartCompact } from '@/components/blocks/chart-blocks'\n\n<ChartCompact variant='area' />`
    },
    {
        id: 'chart-line-compact',
        group: 'blocks',
        category: 'chart-content',
        title: 'Chart/Line/Compact',
        description: 'The same metric card pattern with a line-only plot.',
        height: 480,
        source: CHARTS,
        usage: `import { ChartCompact } from '@/components/blocks/chart-blocks'\n\n<ChartCompact variant='line' />`
    },
    {
        id: 'chart-subscriber-trends',
        group: 'blocks',
        category: 'chart-content',
        title: 'Chart/Line/Multiple',
        description: 'Subscriber trends: a shaded total, one line per channel and a legend that shows or hides each series.',
        height: 900,
        source: CHARTS,
        usage: `import { ChartSubscriberTrends } from '@/components/blocks/chart-blocks'\n\n<ChartSubscriberTrends />`
    },
    {
        id: 'chart-acquisition-channels',
        group: 'blocks',
        category: 'chart-content',
        title: 'Chart/Bar/StackedHorizontal',
        description: 'Acquisition channels as one normalized track with an expandable source list.',
        height: 760,
        source: CHARTS,
        usage: `import { ChartAcquisitionChannels } from '@/components/blocks/chart-blocks'\n\n<ChartAcquisitionChannels />`
    },
    {
        id: 'chart-revenue-overview',
        group: 'blocks',
        category: 'chart-content',
        title: 'Chart/Bar/Revenue',
        description: 'Monthly revenue by source with campaign bands, totals and a toggleable legend.',
        height: 1000,
        source: CHARTS,
        usage: `import { ChartRevenueOverview } from '@/components/blocks/chart-blocks'\n\n<ChartRevenueOverview />`
    },
    {
        id: 'drawer-parts',
        group: 'blocks',
        category: 'drawers',
        title: 'Drawer building blocks',
        description: 'MetricCard/Compact, Field/Row, SupporterRow, the message row, comments, the preview region and the footer recipes.',
        height: 1180,
        source: DRAWERS,
        usage: `import { DrawerMetric, DrawerFieldRow, SupporterRow, MessageRow } from '@/components/blocks/drawer-blocks'\n\n<DrawerMetric label='Revenue' value='$21,000' />\n<DrawerFieldRow label='Field label'><Input defaultValue='Value' /></DrawerFieldRow>\n<SupporterRow name='Oliver Wilson' amount='$2,200' detail='Jan 2' />\n<MessageRow name='Oliver Wilson' date='Jan 2' message="Let's set up a meeting to discuss further." />`
    },
    {
        id: 'recipe-revenue-people',
        group: 'blocks',
        category: 'drawers',
        title: 'Content recipe: Revenue · People',
        description: 'Plugs into SheetBody. People lists fundraisers and their donors; Breakdown splits the revenue by source.',
        height: 1200,
        source: DRAWERS,
        usage: `import { RevenuePeopleContent } from '@/components/blocks/drawer-blocks'\n\n<SheetBody>\n    <RevenuePeopleContent />\n</SheetBody>`
    },
    {
        id: 'recipe-facebook-direct',
        group: 'blocks',
        category: 'drawers',
        title: 'Content recipe: Facebook Direct',
        description: 'The people behind the selected source, with messages awaiting a reply.',
        height: 960,
        source: DRAWERS,
        usage: `import { FacebookDirectContent } from '@/components/blocks/drawer-blocks'\n\n<SheetBody>\n    <FacebookDirectContent />\n</SheetBody>`
    },
    {
        id: 'recipe-acquisition-month',
        group: 'blocks',
        category: 'drawers',
        title: 'Content recipe: January 2026',
        description: 'New supporters by acquisition channel, as a stacked track and an expandable list.',
        height: 900,
        source: DRAWERS,
        usage: `import { AcquisitionMonthContent } from '@/components/blocks/drawer-blocks'\n\n<SheetBody>\n    <AcquisitionMonthContent />\n</SheetBody>`
    },
    {
        id: 'recipe-editor',
        group: 'blocks',
        category: 'drawers',
        title: 'Content recipe: Editor',
        description: 'Fundraiser page fields, page sections, the preview region and comments.',
        height: 1400,
        source: DRAWERS,
        usage: `import { EditorContent } from '@/components/blocks/drawer-blocks'\n\n<SheetBody>\n    <EditorContent onDirtyChange={setDirty} />\n</SheetBody>`
    },
    {
        id: 'kanban-parts',
        group: 'blocks',
        category: 'kanban',
        title: 'Board building blocks',
        description: 'Kanban/Card in default, hover and selected, Kanban/Stages, Kanban/Column, Kanban/OrganizationHeader and Kanban/Toolbar.',
        height: 820,
        source: KANBAN,
        usage: `import { KanbanCard, KanbanColumn, KanbanStages } from '@/components/blocks/kanban'\n\n<KanbanStages />\n<KanbanColumn status='awaiting-review' count={1}>\n    <KanbanCard task={{ title: 'Create social media content', category: 'content', assignee: 'Adelina Hoxha' }} />\n</KanbanColumn>`
    },
    {
        id: 'kanban-swimlane',
        group: 'blocks',
        category: 'kanban',
        title: 'Kanban/Swimlane',
        description: 'One organization, collapsed and expanded. The header reuses Avatar and Badge.',
        height: 420,
        source: KANBAN,
        usage: `import { KanbanSwimlane } from '@/components/blocks/kanban'\n\n<KanbanSwimlane organization={organization} defaultOpen />`
    },
    {
        id: 'kanban-inbox',
        group: 'blocks',
        category: 'kanban',
        title: 'Content/Kanban/Inbox',
        description: 'Drop it into a PageBody. Inbox mirrors the source board.',
        height: 1000,
        source: KANBAN,
        usage: `import { KanbanBoard } from '@/components/blocks/kanban'\n\n<KanbanBoard onSelectTask={openEditor} onAddAction={createAction} />`
    },
    {
        id: 'kanban-empty',
        group: 'blocks',
        category: 'kanban',
        title: 'Content/Kanban/Empty',
        description: 'The starter for a new organization.',
        height: 380,
        source: KANBAN,
        usage: `import { KanbanEmptyBoard } from '@/components/blocks/kanban'\n\n<KanbanEmptyBoard onAddAction={createAction} />`
    },
    {
        id: 'table-default',
        group: 'blocks',
        category: 'tables',
        title: 'Table/Default',
        description: 'Plain records with sortable headers and a result count.',
        height: 460,
        source: TABLES,
        usage: `import { TableDefault } from '@/components/blocks/tables'\n\n<TableDefault />`
    },
    {
        id: 'table-supporters',
        group: 'blocks',
        category: 'tables',
        title: 'Table/Supporters',
        description: 'Selectable rows, people with avatars, row actions, page size and pagination.',
        height: 620,
        source: TABLES,
        usage: `import { TableSupporters } from '@/components/blocks/tables'\n\n<TableSupporters />`
    },
    {
        id: 'table-ai-links',
        group: 'blocks',
        category: 'tables',
        title: 'Table/AiLinks',
        description: 'A header action, a search row, edit and delete per row, and pagination.',
        height: 480,
        source: TABLES,
        usage: `import { TableAiLinks } from '@/components/blocks/tables'\n\n<TableAiLinks />`
    },
    {
        id: 'table-tool-performance',
        group: 'blocks',
        category: 'tables',
        title: 'Table/ToolPerformance',
        description: 'Status, totals with a sparkline, metrics and a See Flow link card.',
        height: 380,
        source: TABLES,
        usage: `import { TableToolPerformance } from '@/components/blocks/tables'\n\n<TableToolPerformance />`
    },
    {
        id: 'table-tool-subscribers',
        group: 'blocks',
        category: 'tables',
        title: 'Table/ToolSubscribers',
        description: 'Totals and dates, then metrics or link cards to the post and its journey.',
        height: 380,
        source: TABLES,
        usage: `import { TableToolSubscribers } from '@/components/blocks/tables'\n\n<TableToolSubscribers />`
    },
    {
        id: 'chat-contact-list',
        group: 'blocks',
        category: 'supporter-response',
        title: 'Chat/ContactList',
        description: 'The title, High-intent, Unread and Done with counts, the filter and the contact rows grouped by day. Pick a row to select it.',
        height: 860,
        source: CHAT_BLOCKS,
        usage: `import { ContactList, ContactListToolbar } from '@/components/blocks/supporter-response'\n\n<ContactList\n    toolbar={<ContactListToolbar tab={tab} onTabChange={setTab} counts={counts} />}\n    items={items}\n    groupLabels={{ yesterday: 'Yesterday' }}\n    onSelect={select}\n/>`
    },
    {
        id: 'chat-conversation-states',
        group: 'blocks',
        category: 'supporter-response',
        title: 'Chat/ConversationPane',
        description: 'Draft, sent and email marked as sent, built from the same thread, bubble, composer and status parts.',
        height: 960,
        source: CHAT_BLOCKS,
        usage: `import { ConversationHeader, ConversationPane } from '@/components/blocks/supporter-response'\n\n<ConversationPane\n    header={<ConversationHeader initials='BS' name='Brandon' summary='Mission statement · Stay involved' done={done} onToggleDone={toggle} />}\n    messages={messages}\n    reply={reply}\n/>`
    },
    {
        id: 'chat-supporter-profile',
        group: 'blocks',
        category: 'supporter-response',
        title: 'Chat/SupporterProfile',
        description: 'Contact details, then tags you can add and remove and AI suggested tags you can accept or dismiss.',
        height: 760,
        source: CHAT_BLOCKS,
        usage: `import { SupporterProfile, SupporterTags } from '@/components/blocks/supporter-response'\n\n<SupporterProfile\n    supporter={supporter}\n    tags={<SupporterTags tags={tags} suggested={suggested} onAdd={add} onRemove={remove} onAccept={accept} onDismiss={dismiss} />}\n/>`
    },
    {
        id: 'chat-workspace',
        group: 'blocks',
        category: 'supporter-response',
        title: 'Chat/Workspace',
        description: 'The three panels together, fully interactive. Drop it into AppShell; the template does exactly that.',
        height: 860,
        source: CHAT_BLOCKS,
        usage: `import { SupporterResponseWorkspace } from '@/components/blocks/supporter-response'\n\n<AppShell title='Supporter Response' icon={<MessengerLogoIcon />} activeItem='supporter-response'>\n    <SupporterResponseWorkspace state='draft' />\n</AppShell>`
    },
    {
        id: 'writing-style-settings',
        group: 'blocks',
        category: 'supporter-response',
        title: 'WritingStyle/Settings',
        description: 'Navigation beside the four sections: writing style and tone, nonprofit details, brand files and websites with the links the AI reads.',
        height: 1040,
        source: WRITING_STYLE,
        usage: `import { WritingStyleDialog, WritingStyleSettings } from '@/components/blocks/writing-style-settings'\n\n<WritingStyleSettings />\n\n// Opened from Set up your writing style:\n<WritingStyleDialog open={open} onOpenChange={setOpen} />`
    },
    pageLayout('layout-one-column', 'one-column', 'One column', 'A single full-width container for forms and long reads.'),
    pageLayout('layout-two-columns', 'two-columns', 'Two columns', 'Two equal containers side by side.'),
    pageLayout('layout-three-columns', 'three-columns', 'Three columns', 'Three equal containers for summaries.'),
    pageLayout('layout-four-columns', 'four-columns', 'Four columns', 'Four equal containers for metrics.'),
    pageLayout('layout-main-aside', 'main-aside', 'Main with aside', 'A wide main container with a narrow aside on the right.'),
    pageLayout('layout-aside-main', 'aside-main', 'Aside with main', 'A narrow aside on the left with a wide main container.'),
    pageLayout('layout-dashboard', 'dashboard', 'Dashboard', 'Four metrics, a full-width section and three cards.'),
    pageLayout('layout-workspace', 'workspace', 'Workspace', 'List, detail and context panels that fill the page edge to edge.'),
    pageLayout('layout-board', 'board', 'Board', 'Four lanes for kanban-style work.'),
    pageLayout('layout-dashboard-dark', 'dashboard-dark', 'Dashboard, dark mode', 'The dashboard on the dark semantic set.', true),
    drawerTemplate('drawer-empty-sm', 'empty-sm', 'Empty drawer, 480px', 'The sheet anatomy at the small width.'),
    drawerTemplate('drawer-empty-default', 'empty-default', 'Empty drawer, 560px', 'The sheet anatomy at the default width.'),
    drawerTemplate('drawer-empty-lg', 'empty-lg', 'Empty drawer, 720px', 'The sheet anatomy at the large width.'),
    drawerTemplate('drawer-revenue-people', 'revenue-people', 'Revenue · People', 'Revenue by fundraiser over the dashboard. Every metric card opens its own drawer.'),
    drawerTemplate('drawer-facebook-direct', 'facebook-direct', 'Facebook Direct', 'The people behind one source. Back returns to January 2026.'),
    drawerTemplate('drawer-acquisition-month', 'acquisition-month', 'January 2026', 'New supporters by acquisition channel.'),
    drawerTemplate('drawer-editor', 'editor', 'Editor', 'Edit a field, then close: you are asked before unsaved edits are discarded.'),
    drawerTemplate('drawer-revenue-people-dark', 'revenue-people', 'Revenue · People, dark', 'The same drawer on the dark semantic set.', true),
    drawerTemplate('drawer-facebook-direct-dark', 'facebook-direct', 'Facebook Direct, dark', 'The same drawer on the dark semantic set.', true),
    drawerTemplate('drawer-acquisition-month-dark', 'acquisition-month', 'January 2026, dark', 'The same drawer on the dark semantic set.', true),
    drawerTemplate('drawer-editor-dark', 'editor', 'Editor, dark', 'The same drawer on the dark semantic set.', true),
    {
        id: 'kanban-template-inbox',
        group: 'templates',
        category: 'kanban',
        title: 'Inbox',
        description: 'Five organizations, one expanded. Open a card or Add action to review it in the editor drawer.',
        height: 900,
        source: KANBAN_TEMPLATES,
        usage: `import { KanbanTemplate } from '@/components/templates/kanban'\n\n<KanbanTemplate kind='inbox' />`
    },
    {
        id: 'kanban-template-empty',
        group: 'templates',
        category: 'kanban',
        title: 'Empty board',
        description: 'The starter for a new organization.',
        height: 620,
        source: KANBAN_TEMPLATES,
        usage: `import { KanbanTemplate } from '@/components/templates/kanban'\n\n<KanbanTemplate kind='empty' />`
    },
    {
        id: 'kanban-template-inbox-dark',
        group: 'templates',
        category: 'kanban',
        title: 'Inbox, dark',
        description: 'The Inbox on the dark semantic set.',
        height: 900,
        dark: true,
        source: KANBAN_TEMPLATES,
        usage: `import { KanbanTemplate } from '@/components/templates/kanban'\n\n<KanbanTemplate kind='inbox' />`
    },
    {
        id: 'kanban-template-empty-dark',
        group: 'templates',
        category: 'kanban',
        title: 'Empty board, dark',
        description: 'The empty board on the dark semantic set.',
        height: 620,
        dark: true,
        source: KANBAN_TEMPLATES,
        usage: `import { KanbanTemplate } from '@/components/templates/kanban'\n\n<KanbanTemplate kind='empty' />`
    },
    supporterResponseTemplate('supporter-response-draft', 'draft', 'Messenger draft', 'GoodUnited AI drafted a reply for Brandon. Edit it, switch to email, send it or mark the conversation done.'),
    supporterResponseTemplate('supporter-response-sent', 'sent', 'Message sent', 'The reply went out and the conversation is marked as done. Write another opens a new draft.'),
    supporterResponseTemplate('supporter-response-email', 'email-marked-sent', 'Email marked as sent', 'The reply was copied into an email and the conversation is marked as done.'),
    supporterResponseTemplate('supporter-response-writing-style', 'writing-style', 'Writing-style settings', 'Opened from Set up your writing style: voice and tone, nonprofit details, brand files and websites.'),
    supporterResponseTemplate('supporter-response-draft-dark', 'draft', 'Messenger draft, dark', 'The same inbox on the dark semantic set.', true),
    supporterResponseTemplate('supporter-response-writing-style-dark', 'writing-style', 'Writing-style settings, dark', 'The settings on the dark semantic set.', true)
]

export const getView = (id: string) => VIEWS.find(view => view.id === id)

export const getCategory = (group: ViewGroup, slug: string) => VIEW_CATEGORIES.find(category => category.group === group && category.slug === slug)

export const viewsIn = (group: ViewGroup, slug: string) => VIEWS.filter(view => view.group === group && view.category === slug && !view.hidden)
